import { Page } from "v2/components";

import { ActivitySetup } from "../shared/ActivitySetup";
import { useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "services/api";

import { ICategory, ITemplate } from "../shared/ActivitySetup/types";
import { useParams } from "react-router-dom";
import { useAuth } from "contexts/auth";
import { ConnectorSetup } from "../shared/ConnectorSetup";
import { LayoutSetup } from "../shared/LayoutSetup";
import { IContractData } from "../shared/ContractDataSetup/types";
import { ContractDataSetup } from "../shared/ContractDataSetup";
import { SetupContext } from "v2/pages/SetupContract/Health/Setup.context";
import {
  IBenefitsPolicy,
  IRules,
  ISetup,
} from "v2/pages/SetupContract/Health/Setup.types";
import { BenefitsPolicy, Rules } from "v2/pages/SetupContract/Health";
import { useAlert } from "contexts/alert";
import { uploadBenefitsPolicyDocument, deleteBenefitsPolicyDocument } from "v2/services/benefitsPolicyDocument.service";
import { ContractLifecycleHeader } from "../shared/ContractLifecycle";

interface IProductData {
  link: string;
  pageTitle: string;
  product: {
    id: string;
    name: string;
  };
  supplier: {
    id: string;
    name: string;
  };
  buyerId: string;
  sellerId: string; // cpnj
}

interface IConnector {
  id: string;
  name: string;
  isExpired: boolean;
}

interface ILayout {
  id: string;
  formats: string[];
  productType: string;
  isConfigured: boolean;
  selectedFormats: string[]; //ID DOS FORMATOS EFETIVAMENTE SELECIONADOS
}

interface ILayoutSupplier {
  name: string;
  layouts: ILayout[];
}

export function EditWorkspace() {
  const { workspaceId } = useParams() as { workspaceId: string };
  const user = useAuth();
  const { openAlert } = useAlert();
  const queryClient = useQueryClient();

  /**
   * DADOS DO WORKSPACE BASE (produto selecionado, etc.)
   */
  const {
    data: productData,
    isFetching: isFetchingProductData,
    isFetched: isFetchedProductData,
    isError: isErrorProductData,
  } = useQuery({
    queryKey: ["workspaceProductData", workspaceId],
    queryFn: () => api.get(`/v2/workspaces/${workspaceId}/details`),
    select: (data) => data.data.data as IProductData,
  });

  /**
   * DADOS DO CONTRATO
   * Criação = cria o objeto aqui
   * Edicao = busca os dados da API
   */
  const [contractData, setContractData] = useState<IContractData>({
    cadastro: {
      cliente: "",
      cnpj: "",
      operadora: "",
      nomeDoProduto: "",
      tipoOperadora: "",
      nContratoApolice: "",
      valorProdutoIOF: "",
      modoDeContratacao: "",
      nVidasInicial: "",
      vigencia: "",
      dataInicioVigencia: "",
      periodoAvisoPrevio: "",
      enderecoFatura: "",
    },
    cobranca: {
      aposentadoCobradoDireto: "",
    },
    faturamento: {
      tipoFatura: "",
      tipoCobranca: "",
      formaPagamento: "",
    },
    operacao: {
      regraMovimentacao: "",
      inicioVigencia: "",
      vencimentoPremio: "",
      emissaoFatura: "",
      tipoDataExclusao: "DISMISSAL_DATE",
    },
    elegibilidade: {
      planos: {
        coparticipacao: "",
        tipoCobertura: "",
        abrangenciaGeografica: "",
      },
      elegibilidade: {
        contribuicao: "",
        contribuicaoTitular: "",
        contribuicaoDependente: "",
        limiteTecnico: "",
        periodoRemissao: "",
        valorTaxa: "",
      },
      caracteristicas: [],
      // caracteristicas: [{ plano: "", codigo: "", valor: "", cargos: "" }, { plano: "", codigo: "", valor: "", cargos: "" }],
      tipo: [],
      // tipo: [{ tipo: "", regra: "", subGrupo: "" }],
    },
    reajusteContrato: {
      inicioAnalise: "",
      fimAnalise: "",
      dataProgramada: "",
      mesReajuste: "",
      periodoAplicacao: "",
      premioAntes: "",
      reajuste: "",
      tipoReajuste: "",
    },
  });

  const {
    data: contractPayload,
    isFetching: isFetchingcontractPayload,
    isFetched: isFetchedcontractPayload,
    isError: isErrorcontractPayload,
  } = useQuery({
    queryKey: ["contractPayload", workspaceId],
    queryFn: () => api.get(`/v2/workspaces/${workspaceId}/data`),
    select: (data) => data.data.data as IContractData,
  });

  useEffect(() => {
    if (contractPayload) {
      setContractData({
        ...contractPayload,
        operacao: {
          ...contractPayload.operacao,
          tipoDataExclusao: contractPayload.operacao?.tipoDataExclusao || 'DISMISSAL_DATE',
        },
      });
    }
  }, [contractPayload]);

  function handleEditContractData(payload: Partial<typeof contractData>) {
    setContractData((prev) => ({ ...prev, ...payload }));
  }

  /**
   * TEMPLATES/CATEGORIES -> CONFIG DE ACTIVITIES
   */
  const [categories, setCategories] = useState<null | ICategory[]>(null);

  const {
    data: templatesData,
    isFetching: isFetchingTemplates,
    isFetched: isFetchedTemplates,
    isError: isErrorTemplates,
  } = useQuery({
    queryKey: ["templates", workspaceId],
    queryFn: () => api.get(`/v2/workspaces/${workspaceId}/activities`),
    select: (data) => data.data.data as ICategory[],
  });

  useEffect(() => {
    if (isFetchedTemplates && templatesData) {
      console.log(
        "📦 EDIT WORKSPACE: Templates carregados da API real",
        templatesData
      );
      setCategories(templatesData);
    }
  }, [templatesData]);

  function handleUpdateCategories(payload: ITemplate) {
    if (!categories) return;

    const payloadCategory = categories.find((category) =>
      category.templates.find((template) => template.name === payload.name)
    );

    if (!payloadCategory) return;

    const newPayloadCategory = {
      ...payloadCategory,
      templates: payloadCategory.templates.map((template) => {
        if (template.name === payload.name) {
          return { ...payload };
        }
        return { ...template };
      }),
    };

    const newCategories = categories.map((category) => {
      if (category.name === payloadCategory.name) {
        return { ...newPayloadCategory };
      }
      return { ...category };
    });

    setCategories((prev) => [...newCategories]);
  }

  /**
   * CONNECTORS
   */
  // const organizationId = "85dc5dfe-afff-4d3f-a4fd-6cfb8c956ac3"; // USADO PARA BUSCAR OS CONNECTORES DA CONTA ATUAL
  const organizationId = user.user?.organization?.cnpj;
  const {
    data: connectors,
    isFetching: isConnectorsFetching,
    isFetched: isConnectorsFetched,
    isError: isConnectorsError,
  } = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.get(`/v2/organizations/${organizationId}/connectors`),
    select: (data) => data.data.data as IConnector[],
  });

  const {
    data: activityConnectors,
    refetch: refetchActivityConnectors,
  } = useQuery({
    queryKey: ["activityConnectors", workspaceId],
    queryFn: () => api.get(`/v2/workspaces/${workspaceId}/activity-connectors`),
    select: (data) => data.data.data as { id: string; connectorName: string; type: string; config: Record<string, unknown> }[],
    enabled: !!workspaceId,
  });

  //Connectores usados nas activities
  const selectedConnectors: string[] = [];
  categories?.forEach((category) => {
    category.templates.forEach((template) => {
      template.activities.forEach((activity) => {
        activity.connectors.forEach((conn) => {
          /** SO PODE FAZER ISSO COM OS QUE O TEMPLATE.ISCONFIGURED = TRUE */
          if (template.isConfigured) selectedConnectors.push(conn.name);
        });
      });
    });
  });

  /**
   * LAYOUTS
   */
  const [layoutsGlobal, setLayouts] = useState<null | ILayoutSupplier[]>(null);
  const [allowedLayouts, setAllowedLayouts] = useState<
    { title: string; id: string }[]
  >([]);
  const {
    data: layoutsData,
    isFetching: isFetchingLayouts,
    isFetched: isFetchedLayouts,
    isError: isErrorLayouts,
  } = useQuery({
    queryKey: ["layouts", workspaceId],
    queryFn: () => api.get(`/v2/workspaces/${workspaceId}/layouts`),
    select: (data) => data.data.data as ILayoutSupplier[],
  });

  useEffect(() => {
    if (isFetchedLayouts && layoutsData) {
      setLayouts(layoutsData);
    }
  }, [layoutsData]);

  // FUNCAO SO PRA ATUALIZAR UI AQUI MESMO PRA ACTIVE
  function handleUpdateLayouts(
    payload: ILayout,
    allowed: { title: string; id: string }
  ) {
    if (!layoutsGlobal) return;

    const newLayouts = layoutsGlobal.map((l) => ({
      ...l,
      layouts: l.layouts.map((lay) => ({
        ...lay,
        ...(lay.id === payload.id && { isConfigured: true }),
      })),
    }));

    setLayouts(newLayouts);
    setAllowedLayouts((prev) => [...prev, allowed]);
  }

  const benefitsPolicy: IBenefitsPolicy = {
    type: "ROLE",
    benefitsPolicyByUnique: {},
    benefitsPolicyByGeographicRegion: [],
    benefitsPolicyByRole: [],
  };
  const rules: IRules = {
    rulesRegister: {},
    rulesCharge: {},
    rulesInvoicing: {},
    rulesOperation: {
      inclusion: {
        rule: "IMEDIATA",
        configImmediate: {},
        configMonthly: {},
        configScheduled: {},
      },
      exclusion: {
        rule: "IMEDIATA",
        exclusionDateType: "DISMISSAL_DATE",
        configImmediate: {},
        configMonthly: {},
        configScheduled: {},
      },
      transference: { mandatoryDocuments: [] },
      change: [],
    },
    rulesElegibility: { elegibilityRules: [], plans: [] },
    rulesReadjustment: {},
  };
  const [setup, setSetup] = useState<ISetup | null>(null);
  /** Remoção de plano com preço confirmada na aba: vai no próximo Salvar e zera quando ele grava. */
  const confirmRemoval = useRef(false);

  const { data: rulesBenefitsPolicy } = useQuery({
    queryKey: ["workspace-rule", workspaceId],
    queryFn: () => api.get(`/v2/new/workspaces/${workspaceId}/fetch`),
    select: (data) => data.data as any,
  });
  useEffect(() => {
    if (rulesBenefitsPolicy) {
      setSetup({
        ...rulesBenefitsPolicy,
        rules: {
          ...rulesBenefitsPolicy.rules,
          rulesOperation: {
            ...rulesBenefitsPolicy.rules.rulesOperation,
            exclusion: {
              ...rulesBenefitsPolicy.rules.rulesOperation.exclusion,
              exclusionDateType: rulesBenefitsPolicy.rules.rulesOperation.exclusion?.exclusionDateType || 'DISMISSAL_DATE',
            },
          },
        },
      });
    }
  }, [rulesBenefitsPolicy]);
  async function handleSavePolicyAndRules(payload: ISetup) {
    try {
      let finalPayload = payload;
      const customDocument = payload.benefitsPolicy?.customDocument as any;

      if (customDocument?.file instanceof File) {
        // Upload the file and replace the File object with the returned documentId
        const uploaded = await uploadBenefitsPolicyDocument(customDocument.file, workspaceId);
        finalPayload = {
          ...payload,
          benefitsPolicy: {
            ...payload.benefitsPolicy,
            customDocument: {
              documentId: uploaded.documentId,
              fileName: uploaded.fileName,
              fileSize: uploaded.fileSize,
            },
          },
        };
      } else if (customDocument?.shouldDelete && customDocument?.documentId) {
        await deleteBenefitsPolicyDocument(customDocument.documentId, workspaceId);
        finalPayload = {
          ...payload,
          benefitsPolicy: {
            ...payload.benefitsPolicy,
            customDocument: undefined,
          },
        };
      }

      await api.post("/v2/new/workspaces/create", {
        setup: finalPayload,
        context: {
          workspaceId: workspaceId,
        },
        ...(confirmRemoval.current && { confirmRemovePlansWithPrices: true }),
      });
      confirmRemoval.current = false;

      setSetup(finalPayload);
      openAlert("Dados salvos com sucesso.", "success");
    } catch (error) {
      // 400 traz a mensagem do backend (plano com preço, código repetido); o resto fica genérico.
      openAlert("Algo deu errado ao salvar os dados", "error", error as AxiosError);
    }
  }

  if (!setup) return <div>Loading...</div>;

  return (
    <>
      {!isFetchedProductData && isFetchingProductData && <div>Loading...</div>}
      {!isFetchedProductData && isErrorProductData && <div>Error...</div>}
      {productData && (
        <SetupContext.Provider
          value={{
            setup: setup,
            updateSetup: handleSavePolicyAndRules,
            workspaceId,
            confirmRemovePlansWithPrices: () => {
              confirmRemoval.current = true;
            },
          }}
        >
          <Page title={productData.pageTitle} backRoute={productData.link} headerExtra={<ContractLifecycleHeader workspaceId={workspaceId} />}>
            <Page.Tab name="Dados do contrato">
              <Rules />
            </Page.Tab>
            <Page.Tab name="Políticas do beneficio">
              <BenefitsPolicy />
            </Page.Tab>
            <Page.Tab name="Atividades">
              <>
                {!isFetchedTemplates && isFetchingTemplates && (
                  <div>Loading...</div>
                )}
                {!isFetchedTemplates && isErrorTemplates && <div>Error...</div>}
                {categories && (
                  <ActivitySetup
                    workspace={{ workspaceId: workspaceId }}
                    allowedLayouts={[]}
                    // allowedLayouts={allowedLayouts}
                    categories={categories}
                    handleUpdateTemplate={handleUpdateCategories}
                  />
                )}
              </>
            </Page.Tab>
            <Page.Tab name="Conectores">
              <>
                {!isConnectorsFetched && isConnectorsFetching && (
                  <div>Loading...</div>
                )}
                {!isConnectorsFetched && isConnectorsError && (
                  <div>Error...</div>
                )}
                {connectors && (
                  <ConnectorSetup
                    connectors={connectors}
                    selectedConnectors={selectedConnectors}
                    activityConnectors={activityConnectors}
                    onConfigSaved={() => refetchActivityConnectors()}
                    workspaceId={workspaceId}
                  />
                )}
              </>
            </Page.Tab>
            <Page.Tab name="Layouts">
              <>
                {!isFetchedLayouts && isFetchingLayouts && (
                  <div>Loading...</div>
                )}
                {!isFetchedLayouts && isErrorLayouts && <div>Error...</div>}
                {layoutsGlobal && (
                  <LayoutSetup
                    layouts={layoutsGlobal}
                    handleUpdateLayouts={handleUpdateLayouts}
                  />
                )}
              </>
            </Page.Tab>
          </Page>
        </SetupContext.Provider>
      )}
    </>
  );
}
